import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { Button } from '../../../../shared/components/index.js';

// L5.02 — Lookup values (add / edit / retire) for one category.
// Also serves L5.03 (reference data viewer) and L5.05 (UoM / currency masters),
// since those are themselves lookup categories in the as-built M05 model.
// Backend:
//   GET  /v1/master/lookup-categories/:categoryId/values
//   POST /v1/master/lookup-categories/:categoryId/values
//   PUT  /v1/master/lookup-values/:valueId            (edit description / sort)
//   PATCH /v1/master/lookup-values/:valueId/deactivate (retire)

interface LookupValue {
  readonly lookupValueId: string;
  readonly lookupValueCode: string;
  readonly description: string;
  readonly sortOrder: number;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
}

export function MasterLookupDetailPage(): ReactNode {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<LookupValue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDesc, setEditDesc] = useState('');

  const cid = categoryId ?? '';

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<LookupValue[]>(`/master/lookup-categories/${encodeURIComponent(cid)}/values`, { token });
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [cid, token]);

  useEffect(() => { void load(); }, [load]);

  const onAdd = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiRequest(`/master/lookup-categories/${encodeURIComponent(cid)}/values`, {
        method: 'POST', token,
        body: { lookupCategoryId: cid, lookupValueCode: code.trim(), description: description.trim(), sortOrder: Number(sortOrder) || 0 },
      });
      setCode(''); setDescription(''); setSortOrder('0');
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [cid, code, description, sortOrder, token, load]);

  const onSaveEdit = useCallback(async (valueId: string) => {
    try {
      await apiRequest(`/master/lookup-values/${encodeURIComponent(valueId)}`, {
        method: 'PUT', token, body: { description: editDesc.trim() },
      });
      setEditingId(null);
      await load();
    } catch (err) { setError((err as Error).message); }
  }, [editDesc, token, load]);

  const onRetire = useCallback(async (valueId: string) => {
    try {
      await apiRequest(`/master/lookup-values/${encodeURIComponent(valueId)}/deactivate`, { method: 'PATCH', token });
      await load();
    } catch (err) { setError((err as Error).message); }
  }, [token, load]);

  return (
    <main aria-label="master-lookup-values">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/admin/mdm/lookups" data-testid="back-catalogue">← {t('mdm.lookupDetail.catalogue')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('mdm.lookupDetail.title')}</h1>
      </div>

      <form onSubmit={onAdd} aria-label="add-value-form"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="val-code" style={{ fontSize: 12 }}>{t('mdm.lookupDetail.valueCode')}</label>
          <input id="val-code" value={code} onChange={(e) => setCode(e.target.value)} required
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="val-desc" style={{ fontSize: 12 }}>{t('mdm.lookup.description')}</label>
          <input id="val-desc" value={description} onChange={(e) => setDescription(e.target.value)} required
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="val-sort" style={{ fontSize: 12 }}>{t('mdm.col.sort')}</label>
          <input id="val-sort" type="number" min={0} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
                 style={{ width: 70, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <Button type="submit" variant="primary" data-testid="add-value" disabled={saving}
                style={{ padding: '6px 14px', borderRadius: 4 }}>
          {saving ? t('mdm.common.adding') : t('mdm.lookupDetail.addValue')}
        </Button>
      </form>
      {formError && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{formError}</p>}

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="values-empty" style={{ color: 'var(--color-text-muted)' }}>{t('mdm.lookupDetail.empty')}</p>
      ) : (
        <DataGrid<any>
          caption="values-table"
          rows={rows}
          getRowId={(v) => v.lookupValueId}
          rowTestId={(v) => `value-row-${v.lookupValueId}`}
          columns={[
            { key: 'code', header: t('mdm.col.code'), value: (v) => v.lookupValueCode },
            {
              key: 'description',
              header: t('mdm.lookup.description'),
              value: (v) => v.description,
              render: (v) => (
                editingId === v.lookupValueId ? (
                  <input data-testid={`edit-input-${v.lookupValueId}`} value={editDesc}
                         onChange={(e) => setEditDesc(e.target.value)}
                         style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
                ) : v.description
              ),
            },
            { key: 'sort', header: t('mdm.col.sort'), value: (v) => v.sortOrder, numeric: true },
            { key: 'status', header: t('mdm.col.status'), value: (v) => v.recordStatusCode },
          ]}
          actionsHeader=""
          actions={(v) => (
            <div style={{ display: 'flex', gap: 8 }}>
              {editingId === v.lookupValueId ? (
                <Button type="button" variant="primary" data-testid={`save-${v.lookupValueId}`}
                        onClick={() => onSaveEdit(v.lookupValueId)}>{t('common.save')}</Button>
              ) : (
                <Button type="button" variant="secondary" data-testid={`edit-${v.lookupValueId}`}
                        onClick={() => { setEditingId(v.lookupValueId); setEditDesc(v.description); }}>{t('mdm.common.edit')}</Button>
              )}
              <Button type="button" variant="danger" data-testid={`retire-${v.lookupValueId}`}
                      onClick={() => onRetire(v.lookupValueId)}>{t('mdm.common.retire')}</Button>
            </div>
          )}
        />
      )}
    </main>
  );
}
